// NewsFeed — flux vertical INFINI d'actualités réelles via Brave backend (v5.7.0).
// Mix 🇨🇮/🌍/🌎 fourni par /api/v3/news/feed. Publicité injectée tous les 4 articles.
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bookmark, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { isArticleSaved, toggleSavedArticle } from "@/lib/savedArticles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// v5.8.0 — Rubriques du fil (déterminées côté backend, jamais inventées).
const RUBRIQUES = [
  { key: null, label: "À la une", emoji: "🗞" },
  { key: "sport", label: "Sport", emoji: "⚽" },
  { key: "economie", label: "Économie", emoji: "📈" },
  { key: "culture", label: "Culture", emoji: "🎭" },
];

// Partage WhatsApp d'un article réel (titre + source + lien).
const shareOnWhatsApp = (a) => {
  const text = `📰 ${a.title}\n${a.source ? `Source : ${a.source}\n` : ""}${a.url}\n\nPartagé via AYA 🇨🇮`;
  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
};

const Skeletons = () => (
  <div style={{ display: "grid", gap: 26 }} data-testid="news-skeletons">
    {[0, 1, 2].map((i) => (
      <div key={i}>
        <div style={{ ...N.img, background: "var(--ah-skeleton, rgba(8,38,24,0.55))", animation: "pulse 1.4s ease-in-out infinite" }} />
        <div style={{ height: 16, width: "85%", borderRadius: 6, margin: "12px 0 8px", background: "var(--ah-skeleton, rgba(8,38,24,0.55))", animation: "pulse 1.4s ease-in-out infinite" }} />
        <div style={{ height: 10, width: "40%", borderRadius: 6, background: "var(--ah-skeleton, rgba(8,38,24,0.5))", animation: "pulse 1.4s ease-in-out infinite" }} />
      </div>
    ))}
  </div>
);

// v5.10.0 — Rendu ÉDITORIAL : plus de carte encadrée. IMAGE immersive → CATÉGORIE compacte
// → TITRE fort → SOURCE · DATE discrète → CTA léger. Aucun excerpt.
const ArticleCard = ({ a }) => {
  const [src, setSrc] = useState(a.image || a.thumbnail || null);
  const [saved, setSaved] = useState(() => isArticleSaved(a.id));
  const onImgError = () =>
    setSrc((cur) => (a.image_fallback && cur !== a.image_fallback ? a.image_fallback : null));
  const toggleSave = () => {
    const added = toggleSavedArticle(a);
    setSaved(added);
    toast.success(added ? "Article enregistré ✓ — onglet Enregistrés" : "Retiré des enregistrés");
  };
  return (
    <article style={N.article} data-testid={`news-article-${a.id}`}>
      {src ? (
        <a href={a.url} target="_blank" rel="noreferrer" style={{ display: "block" }} data-testid={`news-img-link-${a.id}`} aria-label={a.title}>
          <img src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer"
            style={N.img} onError={onImgError} className="aya-news-tap" />
        </a>
      ) : (
        <a href={a.url} target="_blank" rel="noreferrer" style={{ display: "block", textDecoration: "none" }} data-testid={`news-img-link-${a.id}`} aria-label={a.title}>
          <div style={N.fallbackImg} className="aya-news-tap" data-testid={`news-fallback-${a.id}`}>
            <span style={N.fallbackAya}>AYA</span>
            <span style={N.fallbackTag}>{a.flag} {a.tag}</span>
          </div>
        </a>
      )}
      <div style={N.body}>
        {a.tag && <span style={N.tag}>{a.flag} {a.tag}</span>}
        <a href={a.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }} data-testid={`news-read-${a.id}`}>
          <h4 style={N.title} className="aya-news-tap">{a.title}</h4>
        </a>
        <div style={N.meta} data-testid={`news-source-${a.id}`}>
          <span style={{ color: "var(--ah-muted, #8FA79A)", fontWeight: 600 }}>{a.source}</span>
          {a.age_label ? ` · ${a.age_label}` : ""}
        </div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 18, marginTop: 7 }}>
          <button onClick={toggleSave} style={{ ...N.waBtn, color: saved ? "#F59E0B" : "var(--ah-muted, rgba(207,229,214,0.6))" }}
            data-testid={`news-save-${a.id}`} aria-label={saved ? "Retirer des enregistrés" : "Enregistrer l'article"}>
            <Bookmark size={13} fill={saved ? "#F59E0B" : "none"} />
            {saved ? "Enregistré" : "Enregistrer"}
          </button>
          <button onClick={() => shareOnWhatsApp(a)} style={N.waBtn} data-testid={`news-share-wa-${a.id}`} aria-label="Partager sur WhatsApp">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413" />
            </svg>
            Partager
          </button>
        </div>
      </div>
    </article>
  );
};

export const NewsFeed = ({ renderAd }) => {
  const [articles, setArticles] = useState([]);
  const [page, setPage] = useState(0);
  const [status, setStatus] = useState("loading"); // loading | idle | error | end
  const [rubrique, setRubrique] = useState(null);
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);

  const load = useCallback(async (p, rub, reset = false) => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    if (reset) setArticles([]);
    setStatus("loading");
    try {
      const r = await axios.get(`${API}/v3/news/feed`, { params: { page: p, ...(rub ? { rubrique: rub } : {}) }, timeout: 25000 });
      if (!r.data.ok || !Array.isArray(r.data.articles)) throw new Error(r.data.error || "feed");
      setArticles((prev) => {
        const base = reset ? [] : prev;
        const seen = new Set(base.map((a) => a.id));
        return [...base, ...r.data.articles.filter((a) => !seen.has(a.id))];
      });
      setPage(p);
      setStatus(r.data.has_more && r.data.articles.length > 0 ? "idle" : "end");
    } catch {
      setStatus("error");
    } finally {
      loadingRef.current = false;
    }
  }, []);

  useEffect(() => { load(0, null); }, [load]);

  const pickRubrique = (key) => {
    if (key === rubrique || loadingRef.current) return;
    setRubrique(key);
    load(0, key, true);
  };

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || status !== "idle") return;
    const obs = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) load(page + 1, rubrique); },
      { rootMargin: "600px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [status, page, rubrique, load]);

  return (
    <div style={{ display: "grid", gap: 26 }} data-testid="news-feed-vertical">
      {/* Filtres par rubrique */}
      <div style={N.rubriques} data-testid="news-rubriques">
        {RUBRIQUES.map((r) => (
          <button key={r.key || "all"} data-testid={`news-rubrique-${r.key || "all"}`}
            onClick={() => pickRubrique(r.key)}
            style={{ ...N.rubChip, ...(rubrique === r.key ? N.rubChipActive : {}) }}>
            {r.emoji} {r.label}
          </button>
        ))}
      </div>
      {articles.map((a, i) => (
        <Fragment key={a.id}>
          <ArticleCard a={a} />
          {(i + 1) % 4 === 0 && renderAd && (
            <div data-testid={`news-ad-slot-${Math.floor(i / 4)}`}>{renderAd(Math.floor(i / 4))}</div>
          )}
        </Fragment>
      ))}
      {status === "loading" && <Skeletons />}
      {status === "error" && (
        <div style={N.errorBox} data-testid="news-error">
          <p style={{ margin: "0 0 8px", color: "var(--ah-soft, #CFE5D6)", fontSize: 12.5 }}>
            {articles.length ? "Impossible de charger la suite du fil." : "Les actualités sont momentanément indisponibles."}
          </p>
          <button onClick={() => load(articles.length ? page + 1 : 0, rubrique)} style={N.retryBtn} data-testid="news-retry-btn">
            <RefreshCw size={13} /> Réessayer
          </button>
        </div>
      )}
      {status === "end" && articles.length > 0 && (
        <div style={{ textAlign: "center", color: "var(--ah-muted, #6B7F72)", fontSize: 11.5, padding: "8px 0 4px" }}>
          Vous êtes à jour ✓
        </div>
      )}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </div>
  );
};

const N = {
  article: { background: "transparent" },
  img: { width: "100%", aspectRatio: "16 / 9", objectFit: "cover", display: "block", background: "var(--ah-skeleton, #06281A)", borderRadius: 14 },
  fallbackImg: { width: "100%", aspectRatio: "16 / 9", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, background: "radial-gradient(120% 140% at 20% 0%, rgba(16,185,129,0.22), rgba(6,40,26,0.9) 55%, #04120B 100%)", borderRadius: 14 },
  fallbackAya: { fontSize: 26, fontWeight: 900, letterSpacing: 6, color: "rgba(52,211,153,0.5)" },
  fallbackTag: { fontSize: 9.5, fontWeight: 800, letterSpacing: 1, color: "#9DB4A6" },
  body: { padding: "11px 2px 0" },
  tag: { display: "inline-block", fontSize: 10, fontWeight: 800, letterSpacing: 1.4, color: "#34D399", textTransform: "uppercase", marginBottom: 4 },
  title: { margin: "2px 0 6px", fontSize: 21, lineHeight: 1.3, color: "var(--ah-title, #FFFFFF)", fontWeight: 800, display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" },
  meta: { color: "var(--ah-muted, #6B7F72)", fontSize: 12 },
  waBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: 0, border: "none", background: "transparent", color: "var(--ah-accent, rgba(37,211,102,0.75))", fontSize: 11.5, fontWeight: 600, cursor: "pointer" },
  rubriques: { display: "flex", gap: 7, overflowX: "auto", padding: "0 8px 2px", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" },
  rubChip: { flex: "0 0 auto", padding: "6px 13px", borderRadius: 999, border: "1px solid var(--ah-border, rgba(16,185,129,0.3))", background: "var(--ah-chip2-bg, rgba(8,38,24,0.6))", color: "var(--ah-chip2-text, #9DB4A6)", fontSize: 11.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", transition: "background-color .2s, color .2s, border-color .2s" },
  rubChipActive: { background: "var(--ah-chipon-bg, rgba(16,185,129,0.18))", color: "var(--ah-accent, #34D399)", borderColor: "#10B981" },
  errorBox: { borderRadius: 13, padding: 14, textAlign: "center", background: "var(--ah-surface, rgba(8,38,24,0.6))", border: "1px dashed var(--ah-border-strong, rgba(16,185,129,0.35))" },
  retryBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 14px", borderRadius: 999, border: "1px solid var(--ah-border-strong, rgba(16,185,129,0.45))", background: "transparent", color: "var(--ah-accent, #34D399)", fontSize: 12, fontWeight: 700, cursor: "pointer" },
};

export default NewsFeed;
